import { useEffect, useState } from 'react';
import { Modal, Stack, Group, Badge, Alert, Button, Textarea } from '@mantine/core';
import { IconArrowRight, IconAlertCircle } from '@tabler/icons-react';
import { useSurfaceZIndex } from '@ayocore/platform/client/components/surfaceZIndex';
import type { DealStatus } from '../../../../../schemas/deal-status.schema';

interface DealStatusChangeModalProps {
  opened: boolean;
  currentStatus: DealStatus | null | undefined;
  targetStatus: DealStatus | null;
  error: string | null;
  changing: boolean;
  /** Комментарий-причина передаётся для негативных статусов (is_negative). */
  onConfirm: (cancelComment?: string) => void;
  onClose: () => void;
}

/**
 * Модалка подтверждения смены статуса сделки.
 * Показывает переход текущий → новый, ошибку от сервера (если есть).
 *
 * Для негативного целевого статуса (`is_negative` — например «Отменён»)
 * запрашивает причину: она уходит в `deal.cancel_comment`. Обязательность
 * решает сервер (правила инстанса) — его ошибка показывается здесь же.
 */
export function DealStatusChangeModal({
  opened,
  currentStatus,
  targetStatus,
  error,
  changing,
  onConfirm,
  onClose,
}: DealStatusChangeModalProps) {
  // Инлайн-модалка (не через useModal): z-index из общего аллокатора,
  // иначе при открытии DealView в дравере модалка окажется под ним.
  const zIndex = useSurfaceZIndex(opened);
  const needsReason = Boolean(targetStatus?.is_negative);
  const [cancelComment, setCancelComment] = useState('');

  // Каждое открытие модалки — чистое поле: причина относится к конкретному переходу.
  useEffect(() => {
    if (opened) setCancelComment('');
  }, [opened, targetStatus?.id]);
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title='Изменить статус сделки'
      size='sm'
      centered
      closeOnClickOutside={false}
      zIndex={zIndex}
    >
      <Stack gap='md'>
        <Group gap='sm' align='center'>
          {currentStatus ? (
            <Badge color={currentStatus.color ?? 'gray'} variant='light' size='md'>
              {currentStatus.name}
            </Badge>
          ) : (
            <Badge color='gray' variant='light' size='md'>
              Не задан
            </Badge>
          )}

          <IconArrowRight size={16} color='var(--mantine-color-dimmed)' />

          {targetStatus && (
            <Badge color={targetStatus.color ?? 'gray'} variant='filled' size='md'>
              {targetStatus.name}
            </Badge>
          )}
        </Group>

        {needsReason && (
          <Textarea
            label='Причина'
            description='Обязательно опишите, почему сделка не состоялась'
            placeholder='Например: клиент отказался, дорого'
            size='xs'
            autosize
            minRows={2}
            maxRows={4}
            value={cancelComment}
            onChange={event => setCancelComment(event.currentTarget.value)}
            disabled={changing}
          />
        )}

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color='red' variant='light'>
            {error}
          </Alert>
        )}

        <Group justify='flex-end' gap='sm'>
          <Button variant='light' color='gray' size='xs' onClick={onClose} disabled={changing}>
            Отмена
          </Button>
          <Button
            size='xs'
            onClick={() => onConfirm(needsReason ? cancelComment.trim() : undefined)}
            loading={changing}
          >
            Перевести
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
